import { AttachmentCategory } from '@@types/api.types';
import { MainLayout } from '@components/layouts/main.layout';
import { TechnologiesGridTile } from '@components/pages/home/components/technologies-tile.component';
import { ProductFormModal, Steps } from '@components/pages/product/components/product-form-modal.component';
import { ProductHeader } from '@components/pages/product/components/product-header.component';
import { AttachmentsTile } from '@components/shared/attachments/attachments-tile.component';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { ProductsSankeyTile } from '@components/shared/products-sankey.component';
import { useGetProductAttachments } from '@queries/attachments.query';
import { useGetProduct } from '@queries/product.query';
import { useGetCurrentUser } from '@queries/users.query';
import { routes } from '@routes';
import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export const ProductPage: FunctionComponent = () => {
  const { productId } = useParams<{ productId: string }>();
  const { data: product, isPending: isProductLoading } = useGetProduct(productId);
  const { isInnovator } = useGetCurrentUser();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [defaultStep, setDefaultStep] = useState(Steps.DETAILS);

  const handleOpenEditModal = () => {
    setIsEditModalOpen(true);
  };

  const handleClose = () => {
    setIsEditModalOpen(false);
    setDefaultStep(Steps.DETAILS);
  };

  const handleDelete = () => {
    navigate(routes.products.path);
  };

  useEffect(() => {
    if (!isProductLoading && !product) {
      navigate(routes.products.path);
    }
  }, [isProductLoading, product, navigate]);

  const technologyIds = product?.technologies?.map((technology) => technology.id) || [];

  const { data: productAttachments, isLoading: isAttachmentsLoading } = useGetProductAttachments({
    category: AttachmentCategory.GENERAL,
    productId: productId ?? '',
  });

  if (!productId) {
    navigate(routes.home.path);
    return null;
  }

  return (
    <PageviewTracker>
      <HelmetWrapper title={product ? `${product?.name} - Product Details` : 'Product Details'} />
      <MainLayout
        aside={
          technologyIds.length > 0 && (
            <TechnologiesGridTile
              technologyIds={technologyIds}
              title="Product Technologies"
            />
          )
        }
      >
        <div className="w-full space-y-4">
          <ProductHeader
            product={product}
            isLoading={isProductLoading}
            onEditClick={isInnovator ? handleOpenEditModal : undefined}
          />

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
            <AttachmentsTile
              attachments={productAttachments}
              category={AttachmentCategory.GENERAL}
              className="xl:col-span-5"
              isAttachmentsLoading={isAttachmentsLoading}
              targetId={productId}
              targetType="product"
              title="Attachments"
            />

            <div className="xl:col-span-7">
              <ProductsSankeyTile
                productId={productId}
                emptyState={
                  isInnovator
                    ? {
                        title: 'No Product Technologies',
                        message: 'Add a Technology to your Product',
                        onClick: () => {
                          setDefaultStep(Steps.TECHNOLOGIES);
                          handleOpenEditModal();
                        },
                      }
                    : {
                        title: 'No Product Technologies',
                      }
                }
                title="Product Portfolio"
              />
            </div>
          </div>
        </div>
      </MainLayout>

      {isInnovator && isEditModalOpen && (
        <ProductFormModal
          defaultStep={defaultStep}
          isEdit={true}
          isOpen={isEditModalOpen}
          onClose={handleClose}
          onDelete={handleDelete}
          product={product}
        />
      )}
    </PageviewTracker>
  );
};
